import hljs from 'highlight.js'
import { Marked } from 'marked'
import { markedHighlight } from 'marked-highlight'
import type { Item, Quadrant, RadarConfig, Ring } from '../../types'

// Initialize markdown parser
const marked = new Marked(
  markedHighlight({
    langPrefix: 'hljs language-',
    highlight(code, lang) {
      const language = hljs.getLanguage(lang) ? lang : 'plaintext'
      return hljs.highlight(code, { language }).value
    }
  })
)

function convertToHtml(markdown: string, basePath = ''): string {
  // replace deprecated internal links with .html extension
  let content = markdown.replace(/(]\(\/[^)]+)\.html/g, '$1/')

  if (basePath) {
    content = content.replace(/]\(\//g, `](${basePath}/`)
  }

  let html = marked.parse(content.trim()) as string
  html = html.replace(
    /a href="http/g,
    'a target="_blank" rel="noopener noreferrer" href="http'
  )
  return html
}

class Positioner {
  private readonly centerRadius: number
  private readonly minDistance: number = 20
  private readonly paddingRing: number = 15
  private readonly paddingAngle: number = 10
  private positions: Record<string, [number, number][]> = {}
  private ringDimensions: Record<string, [number, number]> = {}
  private quadrantAngles: Record<string, number> = {}

  constructor(
    size: number,
    quadrants: readonly Quadrant[],
    rings: readonly Ring[]
  ) {
    this.centerRadius = size / 2
    const startAngles = [270, 0, 180, 90]

    for (const quadrant of quadrants) {
      this.quadrantAngles[quadrant.id] = startAngles[quadrant.position - 1]
    }

    rings.forEach((ring, index) => {
      const innerRadius =
        (rings[index - 1]?.radius ?? 0) * this.centerRadius + this.paddingRing
      const outerRadius =
        (ring.radius ?? 1) * this.centerRadius - this.paddingRing
      this.ringDimensions[ring.id] = [innerRadius, outerRadius]
    })
  }

  private getDistance(a: [number, number], b: [number, number]): number {
    const [x1, y1] = a
    const [x2, y2] = b
    return Math.sqrt((x1 - x2) ** 2 + (y1 - y2) ** 2)
  }

  private isOverlapping(
    position: [number, number],
    positions: [number, number][]
  ): boolean {
    return positions.some(p => this.getDistance(position, p) < this.minDistance)
  }

  private getXYPosition(
    quadrantId: string,
    ringId: string,
    radiusFraction: number,
    angleFraction: number
  ): [number, number] {
    const [innerRadius, outerRadius] = this.ringDimensions[ringId]
    const ringWidth = outerRadius - innerRadius
    const absoluteRadius = innerRadius + radiusFraction * ringWidth

    const startAngle = this.quadrantAngles[quadrantId] + this.paddingAngle
    const endAngle = startAngle + 90 - 2 * this.paddingAngle
    const absoluteAngle = startAngle + (endAngle - startAngle) * angleFraction
    const angleInRadians = ((absoluteAngle - 90) * Math.PI) / 180

    const x = Math.round(
      this.centerRadius + absoluteRadius * Math.cos(angleInRadians)
    )
    const y = Math.round(
      this.centerRadius + absoluteRadius * Math.sin(angleInRadians)
    )

    return [x, y]
  }

  public getNextPosition(quadrantId: string, ringId: string): [number, number] {
    this.positions[quadrantId] ??= []

    let tries = 0
    let position: [number, number]

    do {
      position = this.getXYPosition(
        quadrantId,
        ringId,
        Math.sqrt(Math.random()),
        Math.random()
      )

      tries++
    } while (
      this.isOverlapping(position, this.positions[quadrantId]) &&
      tries < 150
    )

    this.positions[quadrantId].push(position)

    return position
  }
}

export function processRadarData(
  radarConfig: RadarConfig,
  radarItems: Item[]
): Item[] {
  const quadrantIds = radarConfig.quadrants.map(q => q.id)
  const ringIds = radarConfig.rings.map(r => r.id)
  const positioner = new Positioner(
    radarConfig.chart.size,
    radarConfig.quadrants,
    radarConfig.rings
  )

  // Filter and validate items
  const filteredItems = radarItems.filter(item => {
    if (!item.releases.length) {
      console.warn(`Item ${item.id} has no releases`)
      return false
    }

    const latestRelease = item.releases[item.releases.length - 1]

    if (!item.quadrant || !latestRelease.ring) {
      console.warn(`Item ${item.id} has no quadrant or ring`)
      return false
    }

    if (!quadrantIds.includes(item.quadrant)) {
      console.warn(`Item ${item.id} has invalid quadrant ${item.quadrant}`)
      return false
    }

    if (!ringIds.includes(latestRelease.ring)) {
      console.warn(`Item ${item.id} has invalid ring ${latestRelease.ring}`)
      return false
    }

    return true
  })

  // Process items
  const processedItems = filteredItems.map(item => {
    const latestRelease = item.releases[item.releases.length - 1]

    // Convert markdown to HTML for each release
    const processedReleases = item.releases.map(release => ({
      ...release,
      body: convertToHtml(release.body, radarConfig.basePath)
    }))

    // Get position for the latest release
    const position = positioner.getNextPosition(
      item.quadrant,
      latestRelease.ring
    )

    const processedItem = {
      ...item,
      releases: processedReleases.map(release => ({
        ...release,
        position: release.id === latestRelease.id ? position : release.position
      }))
    }

    if (!processedItem.tags?.length) {
      processedItem.tags = []
    }

    return processedItem
  })

  return processedItems
}
